"use client"

import React, { useId, useMemo, useState } from "react"

import type { AgentInfo } from "@/agents/info"
import { useAgentTransport } from "@/agents/transport"
import { ENTITY_ICONS } from "@/constants/entity-icons"
import { MetricDescriptionLine, useMetricDirectory } from "@/metrics/registry"
import type { MetricRecord } from "@/metrics/runtime"
import { TagDotsPreview } from "@/tags"
import {
  Badge,
  Button,
  Card,
  CardContent,
  CompletionIndicator,
  toast,
} from "@coval/ui"

import { logger } from "@/lib/logger"
import type { SimulatorType } from "@/types/api/SimulatorType"
import { resolveMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import { MetricGrid } from "./MetricGrid"
import { MetricModal } from "./MetricModal"

const MetricsIcon = ENTITY_ICONS.metrics

const log = logger("MetricsSelector")

/** Convert a trpc agent record to the AgentInfo shape expected by MetricRecord.isMetricEnabled. */
const toAgentInfo = (
  agent:
    | {
        display_name: string
        prompt?: string | null
        workflows?: Record<string, unknown> | null
        organization_id?: string
      }
    | null
    | undefined,
): AgentInfo => ({
  display_name: agent?.display_name ?? "",
  prompt: agent?.prompt ?? undefined,
  workflows: agent?.workflows ?? undefined,
  organization_id: agent?.organization_id,
})

interface MetricsSelectorProps {
  selectedMetrics: string[]
  onMetricsChange: (metrics: string[]) => void
  metricRecords: Record<string, MetricRecord>
  simulatorType: SimulatorType
  /** Agent id (22-char primary key only). agents.get is not called if not a valid id. */
  agentId: string
  disabled?: boolean
  showVoice?: boolean
  tagColorMap?: Record<string, string | null | undefined>
  createMetricHref?: string
  createMetricLabel?: string
}

function MetricCategoryBadge({ category }: { category: string | undefined }) {
  if (!category) {
    return null
  }
  return (
    <Badge size="status" className="text-xs">
      {category}
    </Badge>
  )
}

/**
 * MetricsFeature - Clean composition-based metrics selection
 *
 * Provides metrics selection functionality using explicit value/onChange props
 * without context dependencies. Designed for the Simple composition pattern.
 */
export function MetricsSelector({
  selectedMetrics,
  onMetricsChange,
  metricRecords,
  simulatorType,
  agentId,
  disabled = false,
  tagColorMap,
  createMetricHref,
  createMetricLabel,
}: MetricsSelectorProps) {
  const instanceId = useId()
  const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false)
  const [tempSelectedMetrics, setTempSelectedMetrics] = useState<string[]>([])
  // Only call agents.get with 22-char primary key; skip for legacy customer_agent_id or empty
  const isAgentPrimaryKey = agentId.length === 22
  const { data: agentInfo } = useAgentTransport(
    isAgentPrimaryKey ? agentId : undefined,
  )
  const { byManager } = useMetricDirectory()

  // Availability is settled for the whole catalog up front rather than when a
  // row is clicked, so a metric this setup rules out reads as unavailable the
  // moment the picker opens instead of only answering back after a click that
  // could never land.
  const metricErrors = useMemo(() => {
    const agent = toAgentInfo(agentInfo)
    const errors: Record<string, string> = {}
    Object.values(metricRecords ?? {}).forEach((metric) => {
      const { result: canEnable, error_message } = metric.isMetricEnabled(
        agent,
        simulatorType,
      )
      if (!canEnable) {
        errors[metric.metric_name] = error_message
      }
    })
    return errors
  }, [agentInfo, metricRecords, simulatorType])
  // Prefer the catalog's functional category; fall back to the per-instance value.
  // `||` (not `??`) so an empty-string category is treated as missing.
  const getMetricFunctionalCategory = (
    metric: MetricRecord,
  ): string | undefined =>
    byManager[metric.metric_manager ?? ""]?.category || metric.category

  const handleOpenModal = () => {
    if (disabled) {
      return
    }
    setTempSelectedMetrics([...selectedMetrics])
    setIsMetricsModalOpen(true)
  }

  const handleApply = () => {
    onMetricsChange([...tempSelectedMetrics])
    setIsMetricsModalOpen(false)
  }

  const handleCancel = () => {
    setIsMetricsModalOpen(false)
  }

  const handleToggleMetric = (metricName: string, isSelected: boolean) => {
    // The picker already shows these rows as unavailable and withholds their
    // checkbox; this guards the paths that reach a metric name another way.
    // Only adding is refused — a metric already in the selection that this
    // setup rules out has to stay removable.
    if (isSelected && metricErrors[metricName]) {
      return
    }

    if (isSelected) {
      setTempSelectedMetrics((prev) => [...prev, metricName])
    } else {
      setTempSelectedMetrics((prev) =>
        prev.filter((name) => name !== metricName),
      )
    }
  }

  const handleRemoveMetric = (metricId: string) => {
    if (disabled) {
      return
    }
    onMetricsChange(selectedMetrics.filter((id) => id !== metricId))
  }

  // Get agent metrics (metrics associated with this agent)
  const agentMetrics = React.useMemo(() => {
    if (!agentInfo?.metric_ids || !metricRecords) {
      return []
    }
    const agentMetricIdsSet = new Set(agentInfo.metric_ids)
    return Object.values(metricRecords).filter(
      (metric) =>
        metric.id &&
        agentMetricIdsSet.has(metric.id) &&
        !selectedMetrics.includes(metric.metric_name) &&
        // Recommending a metric this simulator type can't score would only
        // offer an "Add" that the picker refuses.
        !metricErrors[metric.metric_name],
    )
  }, [agentInfo, metricErrors, metricRecords, selectedMetrics])

  // Quick add handler for inline metric addition
  const handleQuickAdd = (metricName: string) => {
    if (disabled) {
      return
    }

    // Look up the metric record by metric_name
    const metric = metricRecords[metricName]
    if (!metric) {
      log.warn("Metric not found during quick add", { metricName })
      return
    }

    // Add the metric (validation already done in filtering)
    onMetricsChange([...selectedMetrics, metricName])
    toast.success({
      title: "Metric added",
      description: `${resolveMetricDisplayName(
        metric.metric_display_name,
        metricName,
      )} has been added`,
    })
  }

  const isEmpty = agentMetrics.length === 0 && selectedMetrics.length === 0

  return (
    <>
      <Card className="w-full">
        <div className="flex items-center justify-between gap-4 p-6 pb-4">
          <div className="flex items-center gap-2">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-coval-text">
              <MetricsIcon className="size-4 shrink-0" />
              Metrics
            </h3>
            {selectedMetrics.length > 0 && (
              <span className="text-sm text-coval-text-muted">
                {`${selectedMetrics.length} selected`}
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {selectedMetrics.length > 0 && (
              <Button
                variant="ghost"
                onClick={() => !disabled && onMetricsChange([])}
                disabled={disabled}
                aria-label="Clear all metric selections"
              >
                Clear all
              </Button>
            )}
            {!isEmpty && (
              <Button
                variant="secondary"
                onClick={handleOpenModal}
                disabled={disabled}
                aria-label="Browse metrics"
                data-testid="metrics-browse-button"
              >
                Browse Metrics
              </Button>
            )}
            <CompletionIndicator
              complete={selectedMetrics.length > 0}
              label="Metrics"
            />
          </div>
        </div>
        <CardContent className="space-y-6 px-6 pb-6 pt-0">
          {selectedMetrics.length > 0 && (
            <MetricGrid
              selectedMetrics={selectedMetrics}
              metricRecords={metricRecords}
              onRemoveMetric={handleRemoveMetric}
              instanceId={instanceId}
            />
          )}
          {/* Agent Metrics Section */}
          {agentMetrics.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-coval-text">
                  Agent Metrics
                </h4>
                <Badge size="status">Recommended</Badge>
              </div>
              <div className="grid grid-cols-1 divide-y divide-coval-border">
                {agentMetrics.map((metric) => (
                  <div
                    key={metric.metric_name}
                    className="flex items-center justify-between py-3"
                    onClick={() =>
                      !disabled && handleQuickAdd(metric.metric_name)
                    }
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-medium text-coval-text truncate">
                          {resolveMetricDisplayName(
                            metric.metric_display_name,
                            metric.metric_name,
                          )}
                        </p>
                        <MetricCategoryBadge
                          category={getMetricFunctionalCategory(metric)}
                        />
                      </div>
                      <MetricDescriptionLine
                        metric={metric}
                        className="text-xs text-coval-text-muted line-clamp-2"
                      />
                    </div>
                    <TagDotsPreview
                      tags={metric.tags ?? []}
                      tagColorMap={tagColorMap}
                    />
                    <Button
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleQuickAdd(metric.metric_name)
                      }}
                      disabled={disabled}
                      aria-label={`Add ${metric.metric_display_name || metric.metric_name}`}
                    >
                      Add
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nothing picked and nothing to recommend: point at the catalog. */}
          {isEmpty && (
            <div className="flex flex-col items-center justify-center py-2">
              <div className="text-center space-y-2">
                <Button
                  variant="secondary"
                  onClick={handleOpenModal}
                  disabled={disabled}
                  aria-label="Add metrics"
                  data-testid="metrics-browse-button"
                >
                  Add Metrics
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <MetricModal
        isOpen={isMetricsModalOpen}
        onClose={handleCancel}
        onApply={handleApply}
        metrics={Object.values(metricRecords || {})}
        agentMetricsIds={agentInfo?.metric_ids ?? undefined}
        selectedMetrics={tempSelectedMetrics}
        onToggleMetric={handleToggleMetric}
        instanceId={instanceId}
        metricErrors={metricErrors}
        tagColorMap={tagColorMap}
        createHref={createMetricHref}
        createLabel={createMetricLabel}
      />
    </>
  )
}
